import { useEffect, useMemo, useState } from 'react';
import { Autocomplete, TextField, TextFieldProps } from '@mui/material';
import { useField } from '@unform/core';

interface IVAutoCompleteQtdParcelasProps {
  name: string;
  label?: string;
  min?: number;
  max?: number;
  maxDigitos?: number;
  disabled?: boolean;
  fullWidth?: boolean;
  size?: TextFieldProps['size'];
  sx?: TextFieldProps['sx'];
  calcular?: (novoValor: string | undefined) => void;
}

// Select de quantidade de parcelas que também aceita digitação livre —
// mostra as opções de `min` a `max` (geradas automaticamente, sem lista
// fixa), mas o usuário pode digitar qualquer número em vez de escolher da
// lista (freeSolo), limitado a `maxDigitos` dígitos (ex.: até 999 com o
// padrão de 3). Usado no DialogFaturamento pra "até 36x automático, mas
// pode digitar outro valor".
export const VAutoCompleteQtdParcelas: React.FC<IVAutoCompleteQtdParcelasProps> = ({
  name,
  label = 'Qtd Parcelas',
  min = 1,
  max = 36,
  maxDigitos = 3,
  disabled = false,
  fullWidth,
  size = 'small',
  sx,
  calcular,
}) => {
  const { fieldName, registerField, defaultValue, error, clearError } = useField(name);

  const [value, setValue] = useState<string>(defaultValue != null ? String(defaultValue) : '');

  useEffect(() => {
    registerField({
      name: fieldName,
      getValue: () => value,
      setValue: (_, newValue) => setValue(newValue != null ? String(newValue) : ''),
    });
  }, [registerField, fieldName, value]);

  const opcoes = useMemo(
    () => Array.from({ length: Math.max(0, max - min + 1) }, (_, i) => String(min + i)),
    [min, max]
  );

  const aplicarValor = (novoValor: string) => {
    // Só dígitos, no máximo `maxDigitos` — o Autocomplete não tem um
    // `maxLength` nativo pro digitado livre, então o corte é feito aqui.
    const somenteDigitos = novoValor.replace(/\D/g, '').slice(0, maxDigitos);
    setValue(somenteDigitos);
    calcular?.(somenteDigitos);
    if (somenteDigitos) clearError();
  };

  return (
    <Autocomplete
      freeSolo
      disableClearable
      disabled={disabled}
      options={opcoes}
      // Sem isso, o filtro padrão do MUI compara o texto já digitado
      // (ex.: "1") contra cada opção por substring — e mostra "1, 10, 11,
      // 12..." (tudo que CONTÉM "1") em vez da lista inteira em ordem.
      // `opcoes` já nasce ordenada (1, 2, 3...36), então a lista some
      // sempre exibida assim, independente do que estiver digitado.
      filterOptions={(x) => x}
      inputValue={value}
      onInputChange={(_, novoValor, reason) => {
        if (reason === 'input') aplicarValor(novoValor);
      }}
      onChange={(_, novoValor) => {
        if (typeof novoValor === 'string') aplicarValor(novoValor);
      }}
      // Lista mostra "1x, 2x, 3x..." (na ordem gerada em `opcoes`) — só a
      // apresentação visual do dropdown; o valor por trás (formulário,
      // `calcular`) continua sendo o número puro, sem o "x".
      renderOption={(props, option) => {
        const { key, ...optionProps } = props as typeof props & { key?: React.Key };
        return (
          <li key={key ?? option} {...optionProps}>
            {option}x
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size={size}
          fullWidth={fullWidth}
          label={label}
          error={!!error}
          helperText={error}
          sx={sx}
          inputProps={{
            ...params.inputProps,
            maxLength: maxDigitos,
            inputMode: 'numeric',
          }}
        />
      )}
    />
  );
};
